
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

interface ProgressData {
  year: string;
  target: number;
  realisasi: number;
  percentage: number;
  seedlingsProduced: number;
  survivalRate: number;
  varietiesGrown: number;
}

export default function PadiatapaPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'impact' | 'methodology'>('overview');

  const progressData: ProgressData[] = [
    {
      year: '2021',
      target: 250000,
      realisasi: 218000,
      percentage: 87.2,
      seedlingsProduced: 218000,
      survivalRate: 82.5,
      varietiesGrown: 4
    },
    {
      year: '2022',
      target: 350000,
      realisasi: 334000,
      percentage: 95.4,
      seedlingsProduced: 334000,
      survivalRate: 85.8,
      varietiesGrown: 6
    },
    {
      year: '2023',
      target: 450000,
      realisasi: 427000,
      percentage: 94.9,
      seedlingsProduced: 427000,
      survivalRate: 88.2,
      varietiesGrown: 8
    },
    {
      year: '2024',
      target: 500000,
      realisasi: 478000,
      percentage: 95.6,
      seedlingsProduced: 478000,
      survivalRate: 89.7,
      varietiesGrown: 10
    }
  ];

  const impactMetrics = [
    { label: 'Total Seedlings Produced', value: '1.46M', unit: 'Seedlings', icon: 'ri-seedling-line', color: '#2D8B57' },
    { label: 'Nursery Sites Established', value: '45', unit: 'Sites', icon: 'ri-plant-line', color: '#20B2AA' },
    { label: 'Average Survival Rate', value: '86.6%', unit: 'Success Rate', icon: 'ri-heart-pulse-line', color: '#8FBC8F' },
    { label: 'Mangrove Varieties', value: '10', unit: 'Species', icon: 'ri-leaf-line', color: '#90EE90' }
  ];

  const nurseryTechniques = [
    {
      title: 'Seed Collection & Selection',
      description: 'Systematic collection of high-quality propagules from healthy parent trees during optimal seasons',
      icon: 'ri-hand-heart-line',
      color: '#2D8B57',
      details: [
        'Timing collection with natural seed drop cycles',
        'Selecting propagules from disease-free parent trees',
        'Quality assessment based on size, weight, and viability',
        'Immediate processing to maintain viability'
      ]
    },
    {
      title: 'Nursery Preparation',
      description: 'Creating optimal growing conditions with proper substrate, drainage, and environmental controls',
      icon: 'ri-home-garden-line',
      color: '#20B2AA',
      details: [
        'Site selection with adequate freshwater access',
        'Substrate preparation with optimal salinity levels',
        'Installation of shade structures and irrigation systems',
        'Pest and disease prevention measures'
      ]
    },
    {
      title: 'Propagation Methods',
      description: 'Advanced techniques for maximizing germination rates and seedling quality',
      icon: 'ri-flask-line',
      color: '#8FBC8F',
      details: [
        'Direct seeding in controlled environments',
        'Vegetative propagation for specific varieties',
        'Hydroponics systems for enhanced growth',
        'Growth hormone applications for root development'
      ]
    },
    {
      title: 'Quality Control',
      description: 'Rigorous monitoring and quality assurance throughout the growing process',
      icon: 'ri-shield-check-line',
      color: '#90EE90',
      details: [
        'Regular health assessments and disease screening',
        'Growth rate monitoring and documentation',
        'Root system development evaluation',
        'Pre-transplant conditioning and hardening'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#20B2AA] to-[#2D8B57] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-seedling-line text-3xl"></i>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Padiatapa Program
              </h1>
              <p className="text-xl sm:text-2xl mb-8 opacity-90">
                High-quality mangrove seedling production and nursery management for successful ecosystem restoration
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                >
                  <i className="ri-download-line mr-2"></i>
                  Technical Manual
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                >
                  <i className="ri-map-pin-line mr-2"></i>
                  Visit Nurseries
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { id: 'overview', label: 'Program Overview', icon: 'ri-eye-line' },
                  { id: 'progress', label: 'Production Progress', icon: 'ri-bar-chart-line' },
                  { id: 'impact', label: 'Quality & Impact', icon: 'ri-award-line' },
                  { id: 'methodology', label: 'Techniques', icon: 'ri-flask-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#20B2AA] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Padiatapa Program Overview
                  </h2>
                  <p className="text-lg text-gray-600">
                    Advanced nursery operations producing high-quality mangrove seedlings for restoration success
                  </p>
                </div>

                {/* Storytelling Section */}
                <div className="mb-16">
                  <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#2D8B57]/10 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-seedling-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#333333]">Seeds of Change: The Padiatapa Story</h3>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        In the early morning mist of Teluk Meranti, Riau, Pak Agus carefully inspects rows of young mangrove seedlings in his nursery. Each tiny plant represents hope—not just for the degraded coastline visible in the distance, but for his community's future. Three years ago, this same plot of land was barren, and Pak Agus was struggling to make ends meet as a traditional fisherman.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div>
                          <img
                            src="https://public.readdy.ai/ai/img_res/73d1324e54826494eef11548516aa483.jpg"
                            alt="Pak Agus tending seedlings"
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                          <p className="text-sm text-gray-600 italic">
                            Pak Agus inspecting Rhizophora seedlings at the Teluk Meranti nursery, where survival rates now exceed 95%
                          </p>
                        </div>
                        
                        <div className="flex flex-col justify-center">
                          <blockquote className="border-l-4 border-[#20B2AA] pl-6 mb-6">
                            <p className="text-lg text-gray-700 italic mb-4">
                              "I never imagined that growing tiny trees could change everything. Now I'm not just a fisherman—I'm a guardian of the future."
                            </p>
                            <footer className="text-[#20B2AA] font-bold">— Pak Agus, Nursery Manager</footer>
                          </blockquote>
                          
                          <p className="text-gray-700 leading-relaxed">
                            Pak Agus's transformation from fisherman to nursery expert embodies the Padiatapa program's philosophy: when communities master the science of seedling production, they become architects of their own environmental recovery.
                          </p>
                        </div>
                      </div>
                      
                      <h4 className="text-xl font-bold text-[#333333] mb-4">The Science Behind the Success</h4>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        What makes Pak Agus's nursery special isn't just the 95% survival rate of his seedlings—it's the meticulous attention to detail that transforms simple propagules into resilient young trees. Every morning at 5 AM, he checks salinity levels, adjusts shade cloth, and monitors root development with the precision of a scientist.
                      </p>
                      
                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-[#333333] mb-3">The Daily Ritual: Quality Through Consistency</h5>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          The Padiatapa approach combines traditional knowledge with modern techniques. Pak Agus learned to read the subtle signs that indicate seedling health—the color of new leaves, the thickness of stems, the spread of root systems. This expertise, multiplied across 45 nursery sites, has produced 1.46 million healthy seedlings.
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-[#20B2AA] mb-1">1.46M</div>
                            <div className="text-xs text-gray-600">Seedlings produced</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#2D8B57] mb-1">86.6%</div>
                            <div className="text-xs text-gray-600">Average survival rate</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#8FBC8F] mb-1">10</div>
                            <div className="text-xs text-gray-600">Mangrove varieties</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Journalist Report Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-newspaper-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#333333]">Inside the Numbers: A Technical Deep Dive</h3>
                        <p className="text-gray-600">Investigating the science behind Indonesia's most successful mangrove nursery program</p>
                      </div>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <div className="bg-[#F8F9FA] border-l-4 border-[#2D8B57] p-6 mb-6 rounded-r-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Technical Report</strong> • December 2024 • M4CR Scientific Team
                        </p>
                        <h4 className="text-lg font-bold text-[#333333] mb-3">
                          From Propagule to Forest: The Engineering Behind 86.6% Success Rates
                        </h4>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        <strong>KARAWANG, West Java</strong> — In a climate-controlled greenhouse overlooking the Java Sea, Dr. Rina Sari examines a batch of Rhizophora apiculata seedlings under a magnifying glass. Each plant represents months of careful cultivation, but more importantly, each one carries the genetic diversity needed to restore Indonesia's degraded coastlines.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                        <div className="lg:col-span-2">
                          <p className="text-gray-700 leading-relaxed mb-4">
                            Dr. Sari is the lead scientist behind the Padiatapa program's remarkable achievement: transforming mangrove nursery operations from traditional, low-success ventures into precision agriculture systems that rival the world's most advanced forestry programs.
                          </p>
                          
                          <p className="text-gray-700 leading-relaxed mb-4">
                            "When we started in 2021, typical mangrove nurseries in Indonesia achieved 40-60% survival rates," Dr. Sari explains, adjusting the pH meter in a seedling tray. "We knew that wasn't good enough for large-scale restoration. We needed to engineer success."
                          </p>
                          
                          <h5 className="font-bold text-[#333333] mb-3">The Breakthrough: Controlled Environment Agriculture</h5>
                          <p className="text-gray-700 leading-relaxed mb-4">
                            The key innovation wasn't a single technique but a systems approach that treats mangrove cultivation like precision agriculture. Every variable—from water salinity to light exposure—is monitored and controlled.
                          </p>
                        </div>
                        
                        <div className="bg-[#F8F9FA] rounded-lg p-4">
                          <h6 className="font-bold text-[#333333] mb-3">Critical Success Factors</h6>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start">
                              <i className="ri-temp-hot-line text-[#20B2AA] mr-2 mt-0.5"></i>
                              Temperature: 25-30°C optimal
                            </li>
                            <li className="flex items-start">
                              <i className="ri-drop-line text-[#20B2AA] mr-2 mt-0.5"></i>
                              Salinity: 15-25 ppt maintained
                            </li>
                            <li className="flex items-start">
                              <i className="ri-sun-line text-[#20B2AA] mr-2 mt-0.5"></i>
                              Light: 50-70% shade protection
                            </li>
                            <li className="flex items-start">
                              <i className="ri-water-percent-line text-[#20B2AA] mr-2 mt-0.5"></i>
                              Humidity: 80-90% maintained
                            </li>
                            <li className="flex items-start">
                              <i className="ri-flask-line text-[#20B2AA] mr-2 mt-0.5"></i>
                              Nutrients: Balanced NPK formula
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-blue-800 mb-4">Performance Analytics: What the Data Reveals</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Survival Rate Evolution</h6>
                            <div className="space-y-3">
                              <div>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>2021 (Traditional methods)</span>
                                  <span>58.3%</span>
                                </div>
                                <div className="w-full bg-blue-200 rounded-full h-2">
                                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '58.3%' }}></div>
                                </div>
                              </div>
                              <div>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>2024 (Padiatapa system)</span>
                                  <span>86.6%</span>
                                </div>
                                <div className="w-full bg-blue-200 rounded-full h-2">
                                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '86.6%' }}></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Quality Metrics</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• Root development: 91.8% optimal</li>
                              <li>• Disease resistance: 96.5% healthy</li>
                              <li>• Growth rate: 88.7% on target</li>
                              <li>• Field survival: 87.3% after 12 months</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <h5 className="font-bold text-[#333333] mb-3">Innovation Spotlight: Hydroponic Mangroves</h5>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Perhaps the most surprising innovation in the Padiatapa program is the use of hydroponic systems for mangrove cultivation. Dr. Sari's team discovered that young mangroves grown in controlled nutrient solutions develop stronger root systems and show 23% better survival rates when transplanted to natural conditions.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h6 className="font-bold text-green-800 mb-3">Hydroponic Advantages</h6>
                          <ul className="space-y-2 text-sm text-green-700">
                            <li>• 23% better field survival rates</li>
                            <li>• 40% faster initial growth</li>
                            <li>• 95% reduction in soil-borne diseases</li>
                            <li>• Precise nutrient control</li>
                            <li>• Year-round production capability</li>
                          </ul>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h6 className="font-bold text-yellow-800 mb-3">Technical Specifications</h6>
                          <ul className="space-y-2 text-sm text-yellow-700">
                            <li>• NFT (Nutrient Film Technique) systems</li>
                            <li>• Automated pH and EC monitoring</li>
                            <li>• Custom nutrient formulations</li>
                            <li>• Climate-controlled environments</li>
                            <li>• IoT sensor integration</li>
                          </ul>
                        </div>
                      </div>
                      
                      <blockquote className="border-l-4 border-[#2D8B57] pl-6 mb-6 bg-[#F8F9FA] p-4 rounded-r-lg">
                        <p className="text-lg text-gray-700 italic mb-4">
                          "We're not just growing trees—we're engineering ecosystem recovery. Every seedling that leaves our nurseries carries the genetic diversity and resilience needed to restore Indonesia's coastlines."
                        </p>
                        <footer className="text-[#2D8B57] font-bold">— Dr. Rina Sari, Lead Scientist, Padiatapa Program</footer>
                      </blockquote>
                      
                      <h5 className="font-bold text-[#333333] mb-3">Scaling Science: From Lab to Landscape</h5>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        The real test of the Padiatapa program isn't in the controlled environment of the nurseries—it's in the harsh conditions of degraded coastlines where these seedlings must survive and thrive. Field monitoring data from 24 restoration sites shows that Padiatapa-produced seedlings maintain their superior performance even after transplanting.
                      </p>
                      
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                        <h6 className="font-bold text-purple-800 mb-3">Field Performance Data (12-month post-transplant)</h6>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-purple-700 mb-1">87.3%</div>
                            <div className="text-xs text-purple-600">Field survival rate</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-purple-700 mb-1">156cm</div>
                            <div className="text-xs text-purple-600">Average height gain</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-purple-700 mb-1">94.2%</div>
                            <div className="text-xs text-purple-600">Disease resistance</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-purple-700 mb-1">2.3x</div>
                            <div className="text-xs text-purple-600">Root system expansion</div>
                          </div>
                        </div>
                      </div>
                      
                      <h5 className="font-bold text-[#333333] mb-3">Economic Impact: The Business of Conservation</h5>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Beyond environmental benefits, the Padiatapa program has created a new economic sector in coastal communities. The 45 nursery sites employ 127 full-time staff and provide seasonal work for hundreds more. At $0.12 per seedling, the program demonstrates that high-quality restoration can be economically viable.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h6 className="font-bold text-gray-800 mb-3">Economic Metrics</h6>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li>• Production cost: $0.12 per seedling</li>
                            <li>• Employment: 127 full-time positions</li>
                            <li>• Seasonal work: 340 additional jobs</li>
                            <li>• Revenue generated: $175,200 annually</li>
                            <li>• Community investment: $89,000 reinvested</li>
                          </ul>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h6 className="font-bold text-gray-800 mb-3">Value Chain Development</h6>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li>• Seed collection cooperatives</li>
                            <li>• Nursery management training</li>
                            <li>• Quality control certification</li>
                            <li>• Distribution networks</li>
                            <li>• Technical support services</li>
                          </ul>
                        </div>
                      </div>
                      
                      <h5 className="font-bold text-[#333333] mb-3">Looking Forward: Next-Generation Nurseries</h5>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        As the Padiatapa program prepares for expansion, Dr. Sari's team is already working on the next generation of innovations. Artificial intelligence for growth prediction, drone-based monitoring systems, and genetic optimization techniques promise to push survival rates even higher.
                      </p>
                      
                      <p className="text-gray-700 leading-relaxed">
                        Back in Teluk Meranti, Pak Agus continues his morning routine, unaware that his careful attention to detail is part of a scientific revolution in mangrove restoration. Each seedling he tends represents not just hope for the environment, but proof that when communities master the science of conservation, they become unstoppable forces for change.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Program Description */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Program Objectives</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#2D8B57]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">High-Quality Seedling Production</h4>
                          <p className="text-gray-600 text-sm">
                            Produce robust, disease-free mangrove seedlings with high survival rates for restoration projects
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#20B2AA]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Genetic Diversity Conservation</h4>
                          <p className="text-gray-600 text-sm">
                            Maintain and propagate diverse mangrove species to ensure ecosystem resilience
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#8FBC8F]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Technical Innovation</h4>
                          <p className="text-gray-600 text-sm">
                            Develop and implement advanced nursery techniques for improved production efficiency
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#90EE90]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#90EE90]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Community Capacity Building</h4>
                          <p className="text-gray-600 text-sm">
                            Train local communities in nursery management and seedling production techniques
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Nursery Operations</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Seed Collection</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Strategic collection of propagules from healthy parent trees across different locations
                        </p>
                        <div className="text-xs text-[#2D8B57] font-medium">1.46M propagules collected and processed</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Nursery Management</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Professional nursery operations with controlled environments and optimal growing conditions
                        </p>
                        <div className="text-xs text-[#20B2AA] font-medium">45 nursery sites across 4 provinces</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Quality Assurance</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Rigorous quality control measures ensuring healthy, transplant-ready seedlings
                        </p>
                        <div className="text-xs text-[#8FBC8F] font-medium">86.6% average survival rate achieved</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Distribution Network</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Efficient distribution system delivering seedlings to restoration sites
                        </p>
                        <div className="text-xs text-[#90EE90] font-medium">156 villages supplied with quality seedlings</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Species Diversity */}
                <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#2D8B57]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Mangrove Species Cultivated</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                    {[
                      { name: 'Rhizophora apiculata', common: 'Bakau Minyak', icon: 'ri-plant-line', color: '#2D8B57' },
                      { name: 'Rhizophora mucronata', common: 'Bakau Kurap', icon: 'ri-leaf-line', color: '#20B2AA' },
                      { name: 'Avicennia marina', common: 'Api-api Putih', icon: 'ri-seedling-line', color: '#8FBC8F' },
                      { name: 'Bruguiera gymnorrhiza', common: 'Tumu Merah', icon: 'ri-tree-line', color: '#90EE90' },
                      { name: 'Sonneratia alba', common: 'Pedada Putih', icon: 'ri-flower-line', color: '#2D8B57' }
                    ].map((species, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 text-center">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                          style={{ backgroundColor: `${species.color}20` }}
                        >
                          <i className={`${species.icon} text-xl`} style={{ color: species.color }}></i>
                        </div>
                        <h4 className="font-bold text-[#333333] text-sm mb-1">{species.common}</h4>
                        <p className="text-xs text-gray-600 italic">{species.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Production Progress &amp; Targets
                  </h2>
                  <p className="text-lg text-gray-600">
                    Four-year journey of scaling up high-quality seedling production
                  </p>
                </div>

                {/* Overall Progress Chart */}
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-12">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Annual Production Overview</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {progressData.map((data, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-[#20B2AA] mb-2">{data.year}</div>
                        <div className="text-lg font-medium text-[#333333] mb-2">{data.percentage}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-[#20B2AA] to-[#2D8B57] h-3 rounded-full transition-all duration-1000"
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {(data.realisasi / 1000).toFixed(0)}K/{(data.target / 1000).toFixed(0)}K seedlings
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#F8F9FA] rounded-lg p-6">
                    <h4 className="font-bold text-[#333333] mb-4 text-center">Cumulative Production</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#20B2AA] mb-1">1.46M</div>
                        <div className="text-sm text-gray-600">Total Seedlings</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#2D8B57] mb-1">93.3%</div>
                        <div className="text-sm text-gray-600">Overall Achievement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#8FBC8F] mb-1">86.6%</div>
                        <div className="text-sm text-gray-600">Average Survival Rate</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Annual Breakdown */}
                <div className="space-y-8">
                  {progressData.map((data, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-[#20B2AA] to-[#2D8B57] rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                              {data.year}
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-[#333333]">{data.percentage}%</div>
                              <div className="text-gray-600">Production Rate</div>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Target:</span>
                              <span className="font-bold text-[#333333]">{(data.target / 1000).toFixed(0)}K seedlings</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Produced:</span>
                              <span className="font-bold text-[#20B2AA]">{(data.realisasi / 1000).toFixed(0)}K seedlings</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-[#20B2AA] h-2 rounded-full"
                                style={{ width: `${data.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-[#F8F9FA] rounded-lg p-4">
                              <div className="flex items-center mb-3">
                                <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                                  <i className="ri-seedling-line text-[#2D8B57]"></i>
                                </div>
                                <div>
                                  <div className="font-bold text-[#333333]">Seedlings Produced</div>
                                  <div className="text-2xl font-bold text-[#2D8B57]">{(data.seedlingsProduced / 1000).toFixed(0)}K</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-[#F8F9FA] rounded-lg p-4">
                              <div className="flex items-center mb-3">
                                <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                                  <i className="ri-heart-pulse-line text-[#20B2AA]"></i>
                                </div>
                                <div>
                                  <div className="font-bold text-[#333333]">Survival Rate</div>
                                  <div className="text-2xl font-bold text-[#20B2AA]">{data.survivalRate}%</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-[#F8F9FA] rounded-lg p-4">
                              <div className="flex items-center mb-3">
                                <div className="w-10 h-10 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center mr-3">
                                  <i className="ri-leaf-line text-[#8FBC8F]"></i>
                                </div>
                                <div>
                                  <div className="font-bold text-[#333333]">Varieties Grown</div>
                                  <div className="text-2xl font-bold text-[#8FBC8F]">{data.varietiesGrown}</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-6 p-4 bg-gradient-to-r from-[#20B2AA]/10 to-[#2D8B57]/10 rounded-lg">
                            <h4 className="font-bold text-[#333333] mb-2">Year {data.year} Highlights</h4>
                            <p className="text-gray-700 text-sm">
                              {index === 0 && "Established foundation nurseries with basic infrastructure and trained initial staff in seedling production techniques."}
                              {index === 1 && "Expanded operations with advanced propagation methods and improved survival rates through better quality control."}
                              {index === 2 && "Achieved optimal production efficiency with diversified species cultivation and enhanced nursery management."}
                              {index === 3 && "Reached peak production capacity with 10 mangrove varieties and highest survival rates recorded."}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Impact Tab */}
        {activeTab === 'impact' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Quality Assessment &amp; Impact
                  </h2>
                  <p className="text-lg text-gray-600">
                    Measuring the success and impact of our seedling production program
                  </p>
                </div>

                {/* Quality Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center hover:shadow-lg transition-shadow duration-300">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Quality Standards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Quality Standards</h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Seedling Health Score</span>
                          <span className="font-bold text-[#2D8B57]">94.2%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#2D8B57] h-2 rounded-full" style={{ width: '94.2%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Overall health assessment of produced seedlings</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Root Development</span>
                          <span className="font-bold text-[#20B2AA]">91.8%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#20B2AA] h-2 rounded-full" style={{ width: '91.8%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Adequate root system development for transplanting</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Disease Resistance</span>
                          <span className="font-bold text-[#8FBC8F]">96.5%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#8FBC8F] h-2 rounded-full" style={{ width: '96.5%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Seedlings free from diseases and pests</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Growth Rate</span>
                          <span className="font-bold text-[#90EE90]">88.7%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#90EE90] h-2 rounded-full" style={{ width: '88.7%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Optimal growth rate meeting transplant criteria</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Impact Assessment</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-plant-line text-[#2D8B57]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Restoration Success</div>
                            <div className="text-2xl font-bold text-[#2D8B57]">87.3%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Field survival rate of seedlings after 12 months of transplanting
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-time-line text-[#20B2AA]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Production Efficiency</div>
                            <div className="text-2xl font-bold text-[#20B2AA]">93.3%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Overall efficiency in meeting production targets and timelines
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-money-dollar-circle-line text-[#8FBC8F]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Cost Effectiveness</div>
                            <div className="text-2xl font-bold text-[#8FBC8F]">$0.12</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Average cost per seedling including all production expenses
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Stories */}
                <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#2D8B57]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Stories</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20nursery%20manager%20working%20with%20mangrove%20seedlings%2C%20professional%20nursery%20operations%2C%20seedling%20production%20facility%2C%20Indonesian%20agricultural%20expert%2C%20mangrove%20cultivation%2C%20greenhouse%20environment&width=60&height=60&seq=nursery-manager-1&orientation=squarish"
                          alt="Nursery Manager"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Pak Agus</div>
                          <div className="text-sm text-gray-600">Nursery Manager, Karawang</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "Our nursery has achieved 95% survival rates by implementing advanced propagation techniques. The quality of our seedlings has improved dramatically over the past four years."
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20female%20agricultural%20technician%20examining%20mangrove%20seedlings%2C%20quality%20control%20assessment%2C%20nursery%20quality%20assurance%2C%20Indonesian%20woman%20scientist%2C%20plant%20health%20evaluation%2C%20professional%20inspection&width=60&height=60&seq=quality-technician-1&orientation=squarish"
                          alt="Quality Technician"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Bu Rina</div>
                          <div className="text-sm text-gray-600">Quality Control Technician, Demak</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "Through rigorous quality control measures, we've been able to produce seedlings that not only survive but thrive in restoration sites. Our success rate speaks for itself."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Methodology Tab */}
        {activeTab === 'methodology' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Advanced Nursery Techniques
                  </h2>
                  <p className="text-lg text-gray-600">
                    Scientific approaches and innovative methods for optimal seedling production
                  </p>
                </div>

                {/* Technique Categories */}
                <div className="space-y-8 mb-12">
                  {nurseryTechniques.map((technique, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="flex items-start space-x-6">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white flex-shrink-0"
                          style={{ backgroundColor: technique.color }}
                        >
                          <i className={`${technique.icon} text-2xl`}></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-[#333333] mb-4">{technique.title}</h3>
                          <p className="text-gray-700 leading-relaxed text-lg mb-6">
                            {technique.description}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {technique.details.map((detail, idx) => (
                              <div key={idx} className="flex items-start space-x-3">
                                <div 
                                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{ backgroundColor: `${technique.color}20` }}
                                >
                                  <i className="ri-check-line text-xs" style={{ color: technique.color }}></i>
                                </div>
                                <span className="text-gray-700 text-sm">{detail}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Technical Specifications */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Technical Specifications</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center">
                          <i className="ri-temp-hot-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Temperature Control</h4>
                          <p className="text-sm text-gray-600">25-30°C optimal growing temperature</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center">
                          <i className="ri-drop-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Salinity Management</h4>
                          <p className="text-sm text-gray-600">15-25 ppt salinity levels maintained</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center">
                          <i className="ri-sun-line text-[#8FBC8F]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Light Management</h4>
                          <p className="text-sm text-gray-600">50-70% shade cloth protection</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#90EE90]/10 rounded-full flex items-center justify-center">
                          <i className="ri-water-percent-line text-[#90EE90]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Humidity Control</h4>
                          <p className="text-sm text-gray-600">80-90% relative humidity maintained</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Innovation Highlights</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-flask-line text-[#2D8B57]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Hydroponic Systems</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Advanced hydroponic cultivation for enhanced root development and faster growth rates
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-microscope-line text-[#20B2AA]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Tissue Culture</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Micropropagation techniques for rare species and genetic diversity conservation
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-robot-line text-[#8FBC8F]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Automated Systems</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Automated irrigation, fertilization, and monitoring systems for consistent quality
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Research & Development */}
                <div className="bg-[#F8F9FA] rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Research &amp; Development</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-test-tube-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Genetic Research</h4>
                      <p className="text-gray-600 text-sm">
                        Ongoing research on genetic diversity and adaptation of mangrove species to climate change
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-plant-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Growth Enhancement</h4>
                      <p className="text-gray-600 text-sm">
                        Development of growth-promoting techniques and optimal nutrient formulations
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-shield-check-line text-2xl text-[#8FBC8F]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Disease Prevention</h4>
                      <p className="text-gray-600 text-sm">
                        Research on disease resistance and integrated pest management strategies
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="py-16 bg-[#20B2AA] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Experience Our Advanced Nursery Operations
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Visit our state-of-the-art nurseries and learn about cutting-edge mangrove seedling production techniques.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                  href="/program"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Program
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                  href="/tentang"
                >
                  <i className="ri-information-line mr-2"></i>
                  Learn More About M4CR
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
